import { WatchQueryFetchPolicy } from "@apollo/client";

import { RFXRequestSummary } from "../types/RFXRequestTypes";
import { Contact, Contract } from ".";
import { RFXLineItem } from "./RFXLineItemModel";

export interface RFXRequest {
  rfxRequestRevisionedId: RFXRevisionedId;
  rfxRequestVersion: number;
  rfxRequestClassId: RFXIdentifier;
  rfxResponseClassId: RFXIdentifier;
  rfxLineItemClassId: RFXIdentifier;
  ingestionId?: RFXIdentifier;
  businessId?: RFXIdentifier | null;
  region: RFXIdentifier;
  beginAt: number;
  dueAt: number;
  name: string;
  type: string;
  buyer: RFXIdentifier;
  currency: string;
  quoteTemplateType?: string; // TODO: should be removed as RFXRequest is backend model.
  commodityInformation?: RFXCommodityInformation;
  suppliers: Supplier[];
  properties?: Property[];
  documents?: Document[];
  attributes?: Attribute;
  generalInformation?: GeneralInformation;
  lifeCycle: Lifecycle;
}

export interface RFXResponse {
  rfxResponseRevisionedId: RFXRevisionedId;
  rfxResponseVersion: number;
  rfxRequestRevisionedId: RFXRevisionedId;
  supplierId: RFXIdentifier;
  rfxResponseClassId: RFXIdentifier;
  ingestionId?: RFXIdentifier;
  decisionTime?: number;
  properties: Property[];
  rfxResponseDocuments: Document[];
  contacts: Contact[];
  lifeCycle: Lifecycle;
}

export interface RFXRevisionedId {
  id: RFXIdentifier;
  revision: number;
}

export interface RFXIdentifier {
  value: string;
}

export interface RFXValue {
  valueString: RFXIdentifier;
  dataType: RFXIdentifier;
}

export interface Supplier {
  supplierId: RFXIdentifier;
  supplierName: RFXIdentifier;
  contactId: RFXIdentifier;
  contactSource: string;
  serviceLevelAgreements?: ServiceLevelAgreement[];
  countryCode?: string;
  contracts?: Contract[];
  incoterm?: string;
  qualificationStatus?: string;
  vendorServiceCurrencies?: string[];
  selectedCurrencyValid?: boolean;
}

export interface ServiceLevelAgreement {
  serviceLevelAgreementType: ServiceLevelAgreementType;
  emergencyResponseServiceLevelAgreement: string;
  serviceLevelAgreementProvision: string;
}

export enum ServiceLevelAgreementType {
  DELETE_THIS_LATER
}

export interface Property {
  id: RFXIdentifier;
  value: RFXValue;
}

export interface DownloadRFXRequestAttachmentOutput {
  url: string;
  s3Bucket: string;
  s3Key: string;
  attachment: RFXAttachment;
}

export interface DownloadRFXResponseAttachmentOutput {
  url: string;
  s3Bucket: string;
  s3Key: string;
  attachment: RFXAttachment;
}

export interface DownloadRFXQuestionAttachmentOutput {
  url: string;
  s3Bucket: string;
  s3Key: string;
  attachment: RFXAttachment;
}

/**
 * @deprecated use gql/CreateRFXQuestionAttachmentOutput
 */
export interface CreateRFXQuestionAttachmentOutput {
  externalId: string;
  rfxQuestionId: RFXRevisionedId;
  status: string;
}

export interface Document {
  documentType: string;
  supplierIds?: RFXIdentifier[];
  attachment: RFXAttachment;
}

export interface RFXAttachment {
  externalId: string;
  version?: string;
  filename: string;
  size?: string;
  format?: string;
  description: string;
  stagingS3Uri: string;
  status?: string;
  createdDate?: RFXInstant;
  lastModifiedDate?: RFXInstant;
}

export interface RFXInstant {
  value: string;
  format: string;
}

export interface Attribute {
  acknowledgementRequirement: string;
  ndaRequirement: string;
  participationConfirmationRequirement: string;
}

export interface GeneralInformation {
  overview: string;
  processDetail: string;
}

export interface Lifecycle {
  lifeCycleGraphId: string;
  lifeCycleState: LifeCycleState;
  lifeCycleVersion: number;
}

export interface LifeCycleState {
  name: string;
  version: number;
}

export interface RFXRequestList {
  rfxEvent: RFXRequest[];
}

export interface DocumentUpdateSet {
  updatedAttachmentSet: UpdatedAttachmentSet;
  supplierIds: RFXIdentifier[];
}

export interface UpdatedAttachmentSet {
  filename?: string;
  description?: string;
  stagingS3Uri?: string;
}

export interface RFXCommodityInformation {
  rootId: RFXIdentifier;
  commodityIds: RFXIdentifier[];
  category: RFXIdentifier | undefined;
}

export interface RFXQuoteTemplateGenerationStatus {
  rfxRequestRevisionedId: RFXRevisionedId;
  attempt: number;
  requestState: string;
  message: string | undefined;
  createdAt: number;
  updatedAt: number;
  errorReportS3Key: string | undefined;
  errorReportURL: string | undefined;
  quoteTemplateS3Key: string | undefined;
  quoteTemplateURL: string | undefined;
}

export interface BOMValidationLineItem {
  lineId: string;
  ipn: string;
}

export interface RFXBOMValidationStatus {
  rfxRequestRevisionedId: RFXRevisionedId;
  status: string;
  rfxRequestPublishedAt: number;
  updatedAt: number;
  bomValidationFailedLineItems: BOMValidationLineItem[];
}

// SearchRFXRequest related types

export interface SupplierDetailCondition {
  supplierIdValue?: string;
  contactIdValue?: string;
}

export interface RFXResponseStatusCondition {
  rfxResponseRevisionedIdIdentifier?: string;
  rfxResponseRevisionedIdRevision?: number;
  rfxResponseVersion?: number;
  supplier?: string;
  lifeCycleState?: string;
  comparisonOperator?: RFXComparisonOperator;
}

export interface RFXPropertyCondition {
  propertyId: string
  propertyValue: string
  dataType: string
}

export enum RFXComparisonOperator {
  EQ = "EQ",
  NE = "NE",
  CONTAINS = "CONTAINS",
  NOT_CONTAINS = "NOT_CONTAINS"
}



export interface RFXCondition {
  rfxRequestRevisionedIdIdentifier?: string;
  buyer?: string;
  name?: string;
  type?: string;
  lifeCycleStateName?: string;
  validFromTime?: number;
  validTillTime?: number;
  supplierDetails?: SupplierDetailCondition[];
  rfxResponseStatuses?: RFXResponseStatusCondition[];
  rfxProperties?: RFXPropertyCondition[];
  keyword?: string;
  comparisonOperatorMap?: RFXComparisonOperatorMap;
}

export type RFXComparisonOperatorMap = {[field in keyof RFXCondition]: RFXComparisonOperator};

export type SortOrder = 'ASC' | 'DESC';

export interface SortField {
  order: SortOrder
}

export interface SortCondition {
  rfxRequestClassId?: SortField;
  rfxResponseClassId?: SortField;
  name?: SortField;
  buyer?: SortField;
  lifeCycleStateName?: SortField;
  beginAt?: SortField;
  endAt?: SortField;
}

export interface SearchRFXQuery {
  conditions: RFXCondition[];
  conditionCombinedWith: string;
  sort: SortCondition[];
};


export interface SearchRFXRequestInput {
  query: SearchRFXQuery;
  nextToken?: string;
  maxResults?: number;
  eligibleBusinessList?: string[]
}

export interface SearchRFXRequestOutput {
  rfxRequestSummaryList: RFXRequestSummary[];
  nextToken?: string;
  count: number
}

export interface GetRFXLineItemsForResponseOutput {
  rfxLineItems: RFXLineItem[];
  nextToken?: string | null;
}

export enum RFXDocumentType {
  QUOTE = "QUOTE",
  AWARD = "AWARD",
  DRAWING = "DRAWING",
  TERMS = "TERMS",
  PLAN = "PLAN",
  SPEC = "SPEC",
  MISC = "MISC",
  SUPPORT = "SUPPORT",
  QA = "QA",
  BID = "BID"
}

export interface Employee {
  alias: string;
  emailId: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  department?: string;
}

export interface SearchEmployeeRequestOutput {
  employeeList: Employee[];
  nextToken: string;
  count: number;
}

export interface Notification {
  notificationId: string;
  userId: string;
  rfxRevisionedIdentifier: RFXRevisionedId;
  createdAt: number;
  updatedAt: number;
  notificationText: string;
  notificationCategory: string;
  messageState: string;
  ttl: number
}

export interface GetNotificationsOutput {
  notificationList: Notification[];
  nextToken: string;
}

export interface UpdateNotificationInput {
  notificationId: string;
  messageState: string;
}

export interface GetBulkProcessStatusOutput {
  processStatusList: BulkProcessStatus[];
}

export interface BulkProcessStatus {
  ingestionId: string;
  lineId: string;
  version?: number;
  requestState?: string; // TODO make it enum
  message?: string;
  createdAt?: number;
  updatedAt?: number;
  totalByStateMap?: { [requestState: string]: number }
  updatedEntityIngestionId?: boolean;
  errorReportS3Key?: string;
  errorReportURL?: string;
}

export interface GetBulkProcessParsedContentOutput {
  parsedContentList: BulkProcessParsedContent[];
  nextToken?: string
}

export interface BulkProcessParsedContent {
  ingestionId: string;
  rowIndex: number;
  content: string;
  lineId?: string;
  message?: string;
}

export interface GetIncotermForRFXRequestOutPut {
  incoterm: string;
  nullIncotermReason: string;
}